import { Button } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanManageCatalogItems } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getAddCatalogItemMode } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { getAddInventoryItemMode } from 'features/AddInventoryItem/model/selectors/addInventoryItemSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { useIsAddItemsFlow } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { EmptyResults } from 'shared/ui/EmptyResults';

interface PropertyCatalogEmptyProps {
    selectedCategoryIsEmpty: boolean;
}

export const PropertyCatalogEmpty = ({
    selectedCategoryIsEmpty,
}: PropertyCatalogEmptyProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const isAddItemsFlow = useIsAddItemsFlow();

    const isEditingCatalog = useSelector(getAddCatalogItemMode);
    const isEditingInventory = useSelector(getAddInventoryItemMode);
    const canEditCatalog = useSelector(getUserCanManageCatalogItems);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const propertyId = selectedPropertyId || params.propertyId;
    const showEmptyTextForAddItems =
        !!isEditingInventory || !!isEditingCatalog || isAddItemsFlow;

    const onAddItems = useCallback(() => {
        if (!propertyId) {
            return;
        }

        dispatch(searchCatalogItemsActions.resetSearch());
        dispatch(addCatalogItemActions.setEditItemMode(true));
        navigate(getRouteGlobalCatalog(propertyId));
        dispatch(catalogItemActions.closeDetailedItem());
        dispatch(categoryActions.clearCategorySelection());
    }, [dispatch, navigate, propertyId]);

    if (!selectedCategoryIsEmpty) {
        return (
            <EmptyResults
                title={t('Select a category to view items')}
                dataTestClass="property-catalog-empty"
            />
        );
    }

    const showAddItemsCta = !showEmptyTextForAddItems && canEditCatalog;

    return (
        <EmptyResults
            title={t('No items in this category')}
            description={
                showAddItemsCta
                    ? t(
                          'Click the Add to Catalog button to add items to the category.',
                      )
                    : undefined
            }
            dataTestClass="property-catalog-empty"
            action={
                showAddItemsCta ? (
                    <Button
                        data-testid="btn-add-catalog-items-empty"
                        variant="contained"
                        onClick={onAddItems}
                    >
                        {t('Add items')}
                    </Button>
                ) : undefined
            }
        />
    );
};
